import {
  ChangeDetectionStrategy,
  afterEveryRender,
  Component,
  inject,
  input,
  output,
  signal,
  viewChild,
  computed,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { UploadFilesComponent } from '../../../../../../ui-components/lib/upload-files/upload-files.component';
import { TiptapRichEditorComponent } from '../../../../../../ui-components/lib/tiptap-rich-editor/tiptap-rich-editor.component';
import { FormValidation } from '../../../../../../ui-components/enums/form-validation';
import {
  Attachment,
  UploadedFile,
} from '../../../../../../ui-components/lib/upload-files/attachment.interface';
import { DiscussionEmojiPickerComponent } from '../discussion-emoji-picker/discussion-emoji-picker.component';
import {
  UPLOAD_FILES_URL_KEY,
  uploadFileUrls,
  uploadFileUrlsValidation,
} from '../../../../../common/constants/upload-file-url';
import { Editor } from '@tiptap/core';
import { DiscussionCommentRequest } from '../request-discussion.model';
import { LookupService } from '../../../../../common/services/lookup/lookup.service';
import { MentionSearchFn } from '../../../../../../ui-components/lib/tiptap-rich-editor/mention.model';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'lib-discussion-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    UploadFilesComponent,
    DiscussionEmojiPickerComponent,
    ValidationMessageComponent,
    TiptapRichEditorComponent,
  ],
  templateUrl: './discussion-form.component.html',
  styleUrl: './discussion-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DiscussionFormComponent {
  private readonly lookupService = inject(LookupService);
  private readonly route = inject(ActivatedRoute);

  uploadFilesComponent = viewChild<UploadFilesComponent>(UploadFilesComponent);

  discussionFormGroup = input.required<FormGroup>();
  commentId = input<number>(0);
  initialAttachments = input<Attachment[]>([]);
  triggerClearForm = input<boolean>(false);
  submitAttachmentsOnly = input<boolean>(false);
  addComment = output<DiscussionCommentRequest>();
  updateComment = output<DiscussionCommentRequest>();
  discardForm = output<void>();

  attachments = signal<Attachment[]>([]);
  uploadFileUrls = signal(
    this.route.snapshot.data[UPLOAD_FILES_URL_KEY] ?? uploadFileUrls
  );
  uploadFileUrlsValidation = signal(uploadFileUrlsValidation);
  mentions = signal<any[]>([]);
  tiptapEditor = signal<Editor | null>(null);

  acceptedFileTypes = computed(
    () => uploadFileUrlsValidation.newAcceptedFileTypes
  );

  formValidation = FormValidation;
  private readonly lastTriggerClearForm = signal<boolean>(false);

  get contentControl(): FormControl {
    return this.discussionFormGroup().get('content') as FormControl;
  }

  get attachmentsControl(): FormControl {
    return this.discussionFormGroup().get('attachments') as FormControl;
  }

  get mentionedUsersControl(): FormControl {
    return this.discussionFormGroup().get('mentioned_users') as FormControl;
  }

  constructor() {
    afterEveryRender(() => {
      const shouldClear = this.triggerClearForm();
      if (shouldClear && !this.lastTriggerClearForm()) {
        this.lastTriggerClearForm.set(true);
        this.clearForm();
      } else if (!shouldClear && this.lastTriggerClearForm()) {
        this.lastTriggerClearForm.set(false);
      }
    });
  }

  searchMentions: MentionSearchFn = (query: string, page: number) => {
    return this.lookupService.userLookup({
      name: query,
      page,
      filtersState: 'active',
    });
  };

  onAttachmentsChanged(files: UploadedFile[]): void {
    this.attachments.set(this.adaptAttachments(files));

    const attachmentIds = this.attachments().map(attachment => attachment.id);
    this.attachmentsControl.setValue(
      attachmentIds.length ? attachmentIds : null
    );
  }

  adaptAttachments(files: UploadedFile[]): Attachment[] {
    const attachments: Attachment[] = [];

    for (const file of files) {
      if (file?.id && file?.url) {
        attachments.push({
          id: file.id,
          name: file.file?.name || file.uploadedFile?.name || `File ${file.id}`,
          url: file.url,
          size: file.file?.size || file.uploadedFile?.size || 0,
        });
      }
    }

    return attachments;
  }

  continueAttachments(files: UploadedFile[]): void {
    this.onAttachmentsChanged(files);
    this.onSubmitDiscussion();
  }

  onSubmitDiscussion(): void {
    const formValue = this.discussionFormGroup().getRawValue();

    if (!formValue.content && !formValue?.attachments?.length) {
      this.contentControl.setErrors({ required: true });
      this.discussionFormGroup().markAllAsTouched();
      return;
    }

    if (this.commentId()) {
      this.updateComment.emit(formValue);
    } else {
      this.addComment.emit(formValue);
    }
  }

  clearForm(): void {
    const uploadFilesComponent = this.uploadFilesComponent();
    if (uploadFilesComponent) {
      uploadFilesComponent.files.set([]);
    }
    this.attachments.set([]);
    this.discardForm.emit();
  }
}
